import { filterClasses } from "@swivl-mono/helpers";
import { ActionSheet, ActionSheetAnimationDirection, SearchInput } from "@swivl-mono/ui";
import React, { useEffect, useRef, useState } from "react";

import { BouncingDotsLoader } from "./BouncingDotsLoader";
import { CustomerList } from "./CustomerList";
import { SelectedCustomer } from "./CustomerPickerModal";
import { SelectedCustomerCard } from "./SelectedCustomerCard";
import { useCustomerPicker } from "./useCustomerPicker";

const ANIMATION_DURATION_MS = 350;

interface CustomerPickerActionSheetProps {
  open: boolean;
  onClose: () => void;
  onSelect: (customer: SelectedCustomer) => void;
  selectedCustomer?: SelectedCustomer | null;
  onRemove?: () => void;
  animationDirection?: ActionSheetAnimationDirection;
  onBack?: () => void;
}

export function CustomerPickerActionSheet({
  open,
  onClose,
  onSelect,
  selectedCustomer,
  onRemove,
  animationDirection,
  onBack
}: CustomerPickerActionSheetProps): React.ReactNode {
  // Defer data fetching until after the opening animation finishes
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setReady(false);
      return;
    }
    const timer = setTimeout(() => setReady(true), ANIMATION_DURATION_MS);
    return () => clearTimeout(timer);
  }, [open]);

  const { search, setSearch, users, loading, loadingMore, hasNext, resolvedCustomer, handleRemove, handleSelectUser, loadMore } = useCustomerPicker({
    open: ready,
    onClose,
    onSelect,
    selectedCustomer,
    onRemove,
    pageSize: 20
  });

  const [searchShadow, setSearchShadow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;
    const scrollParent = node.parentElement;
    if (!scrollParent) return;
    const onScroll = (): void => setSearchShadow(scrollParent.scrollTop > 0);
    scrollParent.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => scrollParent.removeEventListener("scroll", onScroll);
  }, [ready]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const canLoadMore = hasNext && !loadingMore && !loading;
  const canLoadMoreRef = useRef(canLoadMore);
  canLoadMoreRef.current = canLoadMore;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && canLoadMoreRef.current) {
          loadMoreRef.current();
        }
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [users.length]);

  return (
    <ActionSheet
      isOpen={open}
      onClose={onClose}
      onBack={onBack}
      animationDirection={animationDirection}
      title="Select Customer"
      className="min-h-[90dvh]"
      maxHeight="90dvh"
    >
      <div ref={contentRef} className="flex flex-col pb-4">
        <div className={filterClasses("sticky top-0 z-10 -mx-4 -mt-1 bg-white px-4 pt-1 pb-3", searchShadow && "shadow-panel")}>
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers..." />
        </div>

        <div className="flex flex-1 flex-col gap-3 pt-3">
          {resolvedCustomer && onRemove && <SelectedCustomerCard name={resolvedCustomer.name} onRemove={handleRemove} variant="mobile" />}

          {loading && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2">
              <BouncingDotsLoader />
              <p className="body-sm text-grey-600">Loading...</p>
            </div>
          )}

          {!loading && users.length === 0 && (
            <div className="pt-1 text-center">
              <p className="body-sm text-grey-600">No customers found</p>
            </div>
          )}

          {!loading && users.length > 0 && (
            <div className="-mx-4">
              <CustomerList users={users} selectedUserId={selectedCustomer?.id} onSelect={handleSelectUser} />
            </div>
          )}

          {loadingMore && (
            <div className="flex flex-col items-center gap-2 py-4">
              <BouncingDotsLoader />
              <p className="body-sm text-grey-600">Loading more customers</p>
            </div>
          )}

          <div ref={sentinelRef} />
        </div>
      </div>
    </ActionSheet>
  );
}
